import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import FormModal from '../components/FormModal';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { storeTradesAPI, type StoreTrade } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Skeleton } from '../components/Skeleton';
import { STORE_STATUS, STORE_STATUS_BADGE } from '../constants/storeStatus';

/**
 * Master Data → Stores → Trades.
 *
 * The trade dropdown on the store form used to be built from the trades that
 * happened to already exist in the data, which meant a brand-new trade could not
 * be picked at all — the control is a <select>. Trades could therefore only enter
 * through bulk upload, where a typo silently became a permanent new option. This
 * tab governs the list instead.
 *
 * `stores.trade` is still a string matched by name, so renaming a trade here
 * rewrites it on every store using it (server-side, in one transaction). The store
 * count on each row is what makes that consequence visible before you commit to it.
 *
 * Whether a trade is still selectable reads in its own Status column: Active, or
 * Inactive once it is retired. Restoring it makes it Active again.
 */

/** The Status column's pill, in the Stores tab's own Active / Inactive colours. */
function TradeStatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
        STORE_STATUS_BADGE[active ? STORE_STATUS.ACTIVE : STORE_STATUS.INACTIVE]
      }`}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function StoreTradesPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [trades, setTrades] = useState<StoreTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState<StoreTrade | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await storeTradesAPI.list(selectedProjectId, true);
      setTrades(res.data?.data || []);
    } catch (err) {
      setError(apiMessage(err, 'Failed to load trades.'));
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingTrade(null);
    setName('');
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (trade: StoreTrade) => {
    setEditingTrade(trade);
    setName(trade.name);
    setFormError(null);
    setIsModalOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;
    setIsSubmitting(true);
    setFormError(null);
    try {
      if (editingTrade) {
        await storeTradesAPI.update(selectedProjectId, editingTrade.id, name);
      } else {
        await storeTradesAPI.create(selectedProjectId, name);
      }
      setIsModalOpen(false);
      await load();
    } catch (err) {
      setFormError(apiMessage(err, 'Failed to save the trade.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleActive = async (trade: StoreTrade) => {
    if (!selectedProjectId) return;
    try {
      await storeTradesAPI.setActive(selectedProjectId, trade.id, !trade.active);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to update the trade.'));
    }
  };

  const addButton = (
    <button
      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      onClick={openCreate}
    >
      Add Trade
    </button>
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Trades</h1>
        </div>
      )}

      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end gap-4">{addButton}</div>
      )}

      {error && <FormErrorBanner message={error} />}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Trade</th>
                <th className="whitespace-nowrap px-6 py-4">Status</th>
                <th className="whitespace-nowrap px-6 py-4">Stores Using It</th>
                <th className="whitespace-nowrap px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                  </tr>
                ))
              ) : trades.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                    No trades yet. Add one to make it selectable on the store form and in
                    the bulk upload.
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.id} className={trade.active ? 'hover:bg-gray-50' : 'bg-gray-50/60 text-gray-400'}>
                    <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                      {trade.name}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <TradeStatusPill active={trade.active} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-gray-600">
                      {trade.storeCount ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${trade.name}`}
                          type="button"
                          onClick={() => openEdit(trade)}
                        />
                        {/* Retire, not delete: stores keep the trade string either
                            way, so deleting would drop the label and leave the data. */}
                        <TableActionButton action={trade.active ? 'deactivate' : 'activate'} label={`${trade.active ? 'Retire' : 'Restore'} ${trade.name}`}
                          type="button"
                          onClick={() => toggleActive(trade)}
                          title={trade.active
                          ? 'Retire — stops it being selectable for new stores; stores already on it are unchanged'
                          : 'Restore this trade to the dropdown'}
                        />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTrade ? 'Edit Trade' : 'Add Trade'}
        size="sm"
        formId="store-trade-form"
        isSubmitting={isSubmitting}
        submitLabel="Save"
      >
        <form id="store-trade-form" onSubmit={submit} className="space-y-4">
          {formError && <FormErrorBanner message={formError} />}

          <div>
            <label htmlFor="trade-name" className="mb-1 block text-sm font-medium text-gray-700">
              Trade Name
            </label>
            <input
              id="trade-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. LFR"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {editingTrade && (editingTrade.storeCount ?? 0) > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Renaming this will also update the trade on{' '}
              <strong>{editingTrade.storeCount}</strong>{' '}
              {editingTrade.storeCount === 1 ? 'store' : 'stores'} currently using it.
            </div>
          )}
        </form>
      </FormModal>
    </div>
  );
}
